import { TimeService } from "@dexteel/mesf-core";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useSectionSpecAtTime } from "@/shared/hooks/useSectionSpec";
import type { SectionSpecDetail } from "@/shared/models/SectionSpec";
import type { InspectionWithValues } from "../../models/Inspection";
import type {
	CreateInspectionReset,
	InspectionFormSubmission,
	InspectionFormValues,
} from "../../models/InspectionForm";
import {
	getItemKeysKey,
	getFormValues,
	parse,
	readingKey,
} from "../../utils/inspectionFormValues";
import { getValidReadings } from "./useLegDeltas";
import { resolveSampleTime } from "../../utils/sampleTime";

export interface UseInspectionFormOptions {
	assetId: number;
	sectionId: number | null;
	currentSectionId: number | null;
	onSpecChange: (detail: SectionSpecDetail | null, pending: boolean) => void;
	editing: InspectionWithValues | null;
	createReset: CreateInspectionReset;
	saving: boolean;
	blocked?: boolean;
	onCancelEdit: () => void;
	onSectionChange: (sectionId: number | null) => void;
	onSubmit: (payload: InspectionFormSubmission) => void;
}

// Keep the form lifecycle above responsive branches so remounting fields preserves edits.
export const useInspectionForm = ({
	assetId,
	sectionId,
	currentSectionId,
	onSpecChange,
	editing,
	createReset,
	saving,
	blocked = false,
	onCancelEdit,
	onSectionChange,
	onSubmit,
}: UseInspectionFormOptions) => {
	const serverTimeZone = TimeService.getInstance().getServerTimeZone();
	const itemKeysRef = useRef<string[]>([]);
	const editingRef = useRef<typeof editing | undefined>(undefined);
	const [resolvedRevisionId, setResolvedRevisionId] = useState<number | null>(
		editing?.ProfileSpecId ?? null,
	);
	const form = useForm<InspectionFormValues>({
		defaultValues: getFormValues(null, sectionId, serverTimeZone, []),
		mode: "onChange",
	});
	const {
		control,
		getValues,
		handleSubmit,
		reset,
		setError,
		setValue,
		formState: { isDirty },
	} = form;
	// Weight is left out on purpose: its field computes its own status, so
	// typing it doesn't re-render the whole form.
	const [inspectedAt, selectedSectionId] = useWatch({
		control,
		name: ["inspectedAt", "sectionId"],
	});
	const inspectionTime = inspectedAt?.isValid() ? inspectedAt.toDate() : null;
	const specQuery = useSectionSpecAtTime(
		selectedSectionId,
		assetId,
		inspectionTime,
	);
	const specReady =
		selectedSectionId !== null &&
		inspectionTime !== null &&
		specQuery.isSuccess &&
		!specQuery.isPreviousData &&
		!specQuery.isFetching &&
		specQuery.isInspectionTimeSettled;
	// The hook retains only same-section data while another sample time resolves.
	const specDetail = specQuery.data ?? null;
	const specPending =
		selectedSectionId === null ||
		inspectionTime === null ||
		!specQuery.isInspectionTimeSettled ||
		specQuery.isPreviousData ||
		specQuery.isFetching ||
		!specQuery.isSuccess;
	const spec = specDetail?.spec ?? null;
	const specItems = specDetail?.items ?? [];
	const itemKeys = specItems
		.filter((item) => item.Applies)
		.map((item) => readingKey(item.ProfileItemId));
	const itemKeysKey = getItemKeysKey(itemKeys);
	const legDeltaMax = spec?.LegDeltaMax ?? null;
	const targetLbsPerFoot = spec?.TargetLbsPerFoot ?? null;
	const minLbsPerFoot = spec?.MinLbsPerFoot ?? null;
	const maxLbsPerFoot = spec?.MaxLbsPerFoot ?? null;
	const editingChanged = editingRef.current !== editing;
	const revisionChanged =
		specReady && resolvedRevisionId !== (spec?.ProfileSpecId ?? null);
	const firstFieldRef = useRef<HTMLInputElement | null>(null);
	const createSessionVersionRef = useRef<number | null>(null);
	const hasResolvedCurrentSectionRef = useRef(false);

	useEffect(() => {
		itemKeysRef.current = itemKeys;
	}, [itemKeys]);

	useEffect(() => {
		onSpecChange(specDetail, specPending);
	}, [onSpecChange, specDetail, specPending]);

	useEffect(() => {
		// Initialize the session before reconciling a query captured by this render.
		// Otherwise a previous sample's ready query can clear restored history.
		if (editingRef.current !== editing) {
			editingRef.current = editing;
			if (editing) {
				setResolvedRevisionId(editing.ProfileSpecId);
				reset(
					getFormValues(
						editing,
						editing.SectionId,
						serverTimeZone,
						itemKeysRef.current,
					),
				);
				return;
			}
		}

		if (
			!editing &&
			(createSessionVersionRef.current !== createReset.version ||
				(!hasResolvedCurrentSectionRef.current && currentSectionId !== null))
		) {
			createSessionVersionRef.current = createReset.version;
			hasResolvedCurrentSectionRef.current = currentSectionId !== null;
			setResolvedRevisionId(null);
			reset(
				getFormValues(
					null,
					currentSectionId,
					serverTimeZone,
					itemKeysRef.current,
				),
			);
			onSectionChange(currentSectionId);
			return;
		}

		if (!specReady) return;
		const id = spec?.ProfileSpecId ?? null;
		if (resolvedRevisionId !== id) {
			setResolvedRevisionId(id);
			const current = getValues("readings");
			setValue(
				"readings",
				Object.fromEntries(
					itemKeysRef.current.map((key) => [key, current[key] ?? ""]),
				),
				{ shouldDirty: true },
			);
		}
	}, [
		createReset.version,
		currentSectionId,
		editing,
		getValues,
		itemKeysKey,
		onSectionChange,
		reset,
		resolvedRevisionId,
		serverTimeZone,
		setValue,
		specReady,
		spec?.ProfileSpecId,
	]);

	const hasPendingChanges = !editing || isDirty;
	// Saving before the spec loads would validate against no dimensions.
	const canSave =
		specReady &&
		!revisionChanged &&
		!editingChanged &&
		(editing !== null ||
			createSessionVersionRef.current === createReset.version) &&
		hasPendingChanges &&
		!saving &&
		!blocked;

	const submit = (values: InspectionFormValues) => {
		if (
			!canSave ||
			!values.inspectedAt?.isValid() ||
			values.sectionId === null ||
			values.heatId === null
		)
			return;
		if (!specReady || spec === null) {
			setError("root.context", {
				message:
					"Select a section with an available specification before saving.",
			});
			return;
		}
		onSubmit({
			inspectedAt: resolveSampleTime(
				values.inspectedAt.toDate(),
				editing !== null || form.getFieldState("inspectedAt").isDirty,
			),
			sectionId: values.sectionId,
			heatId: values.heatId,
			measuredLbsPerFoot: parse(values.weight),
			comments: values.comments.trim() === "" ? null : values.comments.trim(),
			readings: getValidReadings(specItems, values.readings),
		});
		if (!editing) firstFieldRef.current?.focus();
	};

	return {
		assetId,
		editing,
		saving,
		blocked,
		onCancelEdit,
		onSectionChange,
		form,
		serverTimeZone,
		selectedSectionId,
		inspectionTime,
		specQuery,
		specReady,
		spec,
		specItems,
		itemKeys,
		targetLbsPerFoot,
		minLbsPerFoot,
		maxLbsPerFoot,
		legDeltaMax,
		firstFieldRef,
		hasResolvedCurrentSectionRef,
		hasPendingChanges,
		canSave,
		handleSubmit: handleSubmit(submit),
	};
};
